import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/layout/Header';
import PrimaryBox from '../../components/layout/PrimaryBox';
import GnrTable from '../../components/tables/operations/gnr/GnrTable';
import CustomTabs from '../../components/ui/CustomTabs';
import { gnrStateEnum, localStorageKeys } from '../../constants';
import { useGnrRequestsQuery } from '../../hooks/queries/useGnrRequestsQuery';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getItemFromLocalStorage, storeItemToLocalStorage } from '../../utils/localStorage';

const GnrPage: React.FC = () => {
    const { t } = useTranslation();
    usePageTitle(t('gnr').toUpperCase());

    const [selectedTab, setSelectedTab] = useState(parseInt(getItemFromLocalStorage(localStorageKeys.TAB.GNR) || '0'));

    const queryConfigs = [
        { states: [gnrStateEnum.REQUESTED] },
        { states: [gnrStateEnum.IN_PROGRESS] },
        { states: [gnrStateEnum.DELIVERED], includeFiles: true, includeGnrUsages: true },
        { states: [gnrStateEnum.CANCELED] },
        { states: [gnrStateEnum.ARCHIVED], includeFiles: true, includeGnrUsages: true },
    ];

    const {
        data: dataGnrRequests,
        isLoading: isGnrRequestsLoading,
        refetch: refetchGnrRequests,
    } = useGnrRequestsQuery({ queries: queryConfigs[selectedTab] });

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
        storeItemToLocalStorage(localStorageKeys.TAB.GNR, newValue.toString());
    };

    return (
        <PrimaryBox>
            <Header
                title={t('operations')}
                breadcrumbs={[{ label: t('gnr').toUpperCase() }]}
                subTitle={t('gnr').toUpperCase()}
                isDateVisible={false}
            ></Header>

            <CustomTabs
                selectedTab={selectedTab}
                handleTabChange={handleTabChange}
                tabLabels={[t('requested'), t('planned'), t('delivered'), t('canceled'), t('archived')]}
            />
            {selectedTab === 0 && (
                <GnrTable
                    dataGnrRequests={dataGnrRequests?.data?.sort((a, b) => {
                        const dateA = new Date(a.requestDate).setHours(0, 0, 0, 0);
                        const dateB = new Date(b.requestDate).setHours(0, 0, 0, 0);
                        if (dateA < dateB) return 1;
                        if (dateA > dateB) return -1;
                        if (a.requestPeriod < b.requestPeriod) return 1;
                        if (a.requestPeriod > b.requestPeriod) return -1;
                        if (a.siteId.name < b.siteId.name) return 1;
                        if (a.siteId.name > b.siteId.name) return -1;
                        return 0;
                    })}
                    state={gnrStateEnum.REQUESTED}
                    refetch={() => {
                        refetchGnrRequests();
                    }}
                    isLoading={isGnrRequestsLoading}
                    storageKey={localStorageKeys.TABLE.OPERATIONS.GNR.REQUESTED}
                />
            )}
            {selectedTab === 1 && (
                <GnrTable
                    dataGnrRequests={dataGnrRequests?.data?.sort((a, b) => {
                        const dateA = a.plannedDate ? new Date(a.plannedDate).setHours(0, 0, 0, 0) : -1;
                        const dateB = b.plannedDate ? new Date(b.plannedDate).setHours(0, 0, 0, 0) : -1;
                        if (dateA < dateB) return 1;
                        if (dateA > dateB) return -1;
                        if ((a.plannedPeriod || -1) < (b.plannedPeriod || -1)) return 1;
                        if ((a.plannedPeriod || -1) > (b.plannedPeriod || -1)) return -1;
                        if (a.siteId.name < b.siteId.name) return 1;
                        if (a.siteId.name > b.siteId.name) return -1;
                        return 0;
                    })}
                    state={gnrStateEnum.IN_PROGRESS}
                    refetch={() => {
                        refetchGnrRequests();
                    }}
                    isLoading={isGnrRequestsLoading}
                    storageKey={localStorageKeys.TABLE.OPERATIONS.GNR.PLANNED}
                />
            )}
            {selectedTab === 2 && (
                <GnrTable
                    dataGnrRequests={dataGnrRequests?.data?.sort((a, b) => {
                        const dateA = a.deliveryDate ? new Date(a.deliveryDate).setHours(0, 0, 0, 0) : -1;
                        const dateB = b.deliveryDate ? new Date(b.deliveryDate).setHours(0, 0, 0, 0) : -1;
                        if (dateA < dateB) return 1;
                        if (dateA > dateB) return -1;
                        if (a.siteId.name < b.siteId.name) return 1;
                        if (a.siteId.name > b.siteId.name) return -1;
                        return 0;
                    })}
                    state={gnrStateEnum.DELIVERED}
                    refetch={() => refetchGnrRequests()}
                    isLoading={isGnrRequestsLoading}
                    storageKey={localStorageKeys.TABLE.OPERATIONS.GNR.DELIVERED}
                />
            )}
            {selectedTab === 3 && (
                <GnrTable
                    dataGnrRequests={dataGnrRequests?.data?.sort((a, b) => {
                        const dateA = new Date(a.requestDate).setHours(0, 0, 0, 0);
                        const dateB = new Date(b.requestDate).setHours(0, 0, 0, 0);
                        if (dateA < dateB) return 1;
                        if (dateA > dateB) return -1;
                        if (a.requestPeriod < b.requestPeriod) return 1;
                        if (a.requestPeriod > b.requestPeriod) return -1;
                        if (a.siteId.name < b.siteId.name) return 1;
                        if (a.siteId.name > b.siteId.name) return -1;
                        return 0;
                    })}
                    state={gnrStateEnum.CANCELED}
                    refetch={() => refetchGnrRequests()}
                    isLoading={isGnrRequestsLoading}
                    storageKey={localStorageKeys.TABLE.OPERATIONS.GNR.CANCEL}
                />
            )}
            {selectedTab === 4 && (
                <GnrTable
                    dataGnrRequests={dataGnrRequests?.data?.sort((a, b) => {
                        const dateA = new Date(a.requestDate).setHours(0, 0, 0, 0);
                        const dateB = new Date(b.requestDate).setHours(0, 0, 0, 0);
                        if (dateA < dateB) return 1;
                        if (dateA > dateB) return -1;
                        if (a.requestPeriod < b.requestPeriod) return 1;
                        if (a.requestPeriod > b.requestPeriod) return -1;
                        if (a.siteId.name < b.siteId.name) return 1;
                        if (a.siteId.name > b.siteId.name) return -1;
                        return 0;
                    })}
                    state={gnrStateEnum.ARCHIVED}
                    refetch={() => refetchGnrRequests()}
                    isLoading={isGnrRequestsLoading}
                    storageKey={localStorageKeys.TABLE.OPERATIONS.GNR.ARCHIVED}
                />
            )}
        </PrimaryBox>
    );
};

export default GnrPage;
